import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../api/client';
import { blogApi, type BlogTag } from '../../api/blog';
import { isValidSlug, slugify } from '../../lib/slug';
import BlogNav from './BlogNav';

export default function BlogTagsPage() {
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<BlogTag | null>(null);
  const [slug, setSlug] = useState('');
  const [nameBg, setNameBg] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: tags, isPending } = useQuery({ queryKey: ['blog-tags'], queryFn: blogApi.tags });

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  }

  function reset() {
    setEditing(null);
    setSlug('');
    setNameBg('');
    setNameEn('');
    setError(null);
  }

  const save = useMutation({
    mutationFn: () =>
      editing
        ? blogApi.updateTag(editing.id, { slug: editing.slug, nameBg, nameEn })
        : blogApi.createTag({ slug, nameBg, nameEn }),
    onSuccess: async () => {
      reset();
      await queryClient.invalidateQueries({ queryKey: ['blog-tags'] });
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => blogApi.removeTag(id),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['blog-tags'] });
    },
    onError: fail,
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  const slugOk = editing !== null || isValidSlug(slug);

  return (
    <>
      <BlogNav />

      <section className="card">
        <h2>Tags</h2>
        <p className="muted">
          A small, fixed vocabulary. Posts — and the article generator — may only use tags from this list, so a topic
          stays one page instead of splitting into "осигуровки", "Осигуровки" and "осигурителни вноски", each with one
          article under it. A tag page is kept out of search until it has three posts.
        </p>

        {error && <p className="status-error">{error}</p>}
        {isPending && <p className="muted">Loading…</p>}

        {tags && tags.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Slug</th>
                  <th>Bulgarian</th>
                  <th>English</th>
                  <th>Posts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <tr key={tag.id}>
                    <td className="muted">{tag.slug}</td>
                    <td>{tag.nameBg}</td>
                    <td>{tag.nameEn}</td>
                    <td>{tag.postCount}</td>
                    <td>
                      <div className="toolbar">
                        <button
                          type="button"
                          className="btn btn--small"
                          onClick={() => {
                            setEditing(tag);
                            setSlug(tag.slug);
                            setNameBg(tag.nameBg);
                            setNameEn(tag.nameEn);
                            setError(null);
                          }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="btn btn--small"
                          disabled={tag.postCount > 0 || remove.isPending}
                          title={tag.postCount > 0 ? 'Still used by posts' : undefined}
                          onClick={() => remove.mutate(tag.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h3>{editing ? `Rename "${editing.slug}"` : 'New tag'}</h3>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label className="field__label" htmlFor="tagBg">
              Name in Bulgarian
            </label>
            <input
              id="tagBg"
              className="field__input"
              value={nameBg}
              maxLength={100}
              onChange={(e) => {
                setNameBg(e.target.value);
                // The slug follows the Bulgarian name until the tag exists; after that it is a URL.
                if (!editing) {
                  setSlug(slugify(e.target.value));
                }
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="tagEn">
              Name in English
            </label>
            <input id="tagEn" className="field__input" value={nameEn} maxLength={100} onChange={(e) => setNameEn(e.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="tagSlug">
              Slug — /blog/tag/<strong>{slug || '…'}</strong>
            </label>
            <input
              id="tagSlug"
              className="field__input"
              value={slug}
              spellCheck={false}
              disabled={editing !== null}
              onChange={(e) => setSlug(e.target.value)}
            />
            {editing && <span className="muted">Posts reference the slug and it is a URL, so it cannot change.</span>}
            {!slugOk && slug !== '' && (
              <span className="status-error">Lower-case Latin letters, digits and single dashes only.</span>
            )}
          </div>

          <div className="toolbar">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={save.isPending || !slugOk || nameBg.trim() === '' || nameEn.trim() === ''}
            >
              {editing ? 'Save' : 'Add tag'}
            </button>
            {editing && (
              <button type="button" className="btn" onClick={reset}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>
    </>
  );
}
